import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Warning, Sparkle, EnvelopeSimple, Trophy } from "@phosphor-icons/react";

const fmt = (n) => "$" + Math.round(n || 0).toLocaleString();
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const SEV = {
  critical: "border-red-500/60 bg-red-500/10",
  warning:  "border-yellow-500/60 bg-yellow-500/10",
  optimize: "border-emerald-500/60 bg-emerald-500/10",
};

export default function AdminAudits() {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      const r = await api.get("/admin/policy-audits");
      setAudits(r.data?.audits || []);
      if ((r.data?.audits || []).length && !selected) setSelected(r.data.audits[0]);
    } catch {
      toast.error("Could not load audits. Sign in as advisor.");
    } finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-audits-page">
        <div className="mb-8">
          <p className="eyebrow"><span className="gold-divider" />Admin · Policy Audits</p>
          <h1 className="font-display text-4xl mt-3">Every audit, every finding, every FYC.</h1>
          <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
            Full advisor view of every Policy Audit clients have submitted, with carrier stack, structure, layering plan, close strategy, and projected first-year commission per finding.
          </p>
        </div>

        {loading ? (
          <p className="text-exodus-muted text-sm">Loading…</p>
        ) : audits.length === 0 ? (
          <div className="card-surface p-10 text-center text-exodus-muted">
            No audits submitted yet. When clients complete the Full Policy Audit, they'll appear here automatically.
          </div>
        ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 card-surface divide-y divide-exodus-border max-h-[75vh] overflow-auto">
            {audits.map((a, i) => (
              <button
                key={i}
                data-testid={`audit-row-${i}`}
                onClick={() => setSelected(a)}
                className={`w-full px-5 py-4 flex flex-col text-left transition-colors ${selected === a ? "bg-exodus-elevated border-l-2 border-exodus-gold" : "hover:bg-exodus-elevated"}`}
              >
                <p className="text-sm truncate flex items-center gap-2">
                  <EnvelopeSimple size={12} className="text-exodus-muted" /> {a.email}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-xs eyebrow gold-text">Score {a.advisor_view.score}/100</span>
                  <span className="text-[10px] text-exodus-muted">{fmtDate(a.created_at)}</span>
                </div>
                <p className="text-[11px] text-emerald-300 mt-1">FYC ~{fmt(a.advisor_view.total_fyc_projected)}</p>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            {selected && <AuditDetail a={selected} />}
          </div>
        </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function AuditDetail({ a }) {
  const adv = a.advisor_view;
  const inp = a.input;
  return (
    <div className="card-surface p-6 lg:p-8 space-y-6">
      <div>
        <p className="eyebrow text-xs">Client</p>
        <h2 className="font-display text-2xl mt-1">{a.email}</h2>
        <div className="flex flex-wrap gap-4 mt-3 text-xs text-exodus-muted">
          <span>Age {inp.age} · {inp.sex} · {(inp.health_class || "").replace("_"," ")}</span>
          <span>{inp.marital_status} · {inp.dependents} dep.</span>
          <span>Income {fmt(inp.annual_income)}/yr · saves {fmt(inp.monthly_savings_capacity)}/mo</span>
          <span>Liquid {fmt(inp.liquid_assets)} · Retirement {fmt(inp.retirement_assets)}</span>
          <span>Goal: {inp.primary_goal} · Retire {inp.retirement_target_age}</span>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Audit score" value={`${adv.score}/100`} accent="gold" />
        <Stat label="Findings" value={adv.findings.length} />
        <Stat label="Total projected FYC" value={fmt(adv.total_fyc_projected)} accent="emerald" />
      </div>

      <div className="border-t border-exodus-border pt-4">
        <p className="eyebrow text-xs text-exodus-muted mb-2">Summary</p>
        <p className="text-sm">{adv.summary}</p>
      </div>

      <div className="border-t border-exodus-border pt-4">
        <p className="eyebrow text-xs text-exodus-muted mb-3 flex items-center gap-2">
          <Trophy size={12} weight="fill" className="text-exodus-gold" /> Close strategy
        </p>
        <p className="text-sm leading-relaxed bg-exodus-elevated border-l-2 border-exodus-gold pl-4 py-2">{adv.close_strategy}</p>
      </div>

      <div className="border-t border-exodus-border pt-4">
        <p className="eyebrow text-xs text-exodus-muted mb-3">Findings</p>
        <div className="space-y-3">
          {adv.findings.map((f, i) => {
            const Ic = f.severity === "optimize" ? Sparkle : Warning;
            return (
              <div key={i} className={`border-l-4 ${SEV[f.severity] || SEV.optimize} p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <Ic size={14} weight="fill" className="text-exodus-gold" />
                  <span className="text-[10px] eyebrow text-exodus-muted">{f.severity} · {f.category}</span>
                </div>
                <p className="font-display text-base">{f.headline}</p>
                <p className="text-xs text-exodus-muted leading-relaxed mt-2">{f.finding}</p>
                <div className="mt-3 pt-3 border-t border-exodus-border grid sm:grid-cols-2 gap-3 text-xs">
                  <MetaBlock label="Client action" value={f.action} />
                  <MetaBlock label="Carrier stack" value={(f.carrier_stack || []).join(", ") || "—"} />
                  <MetaBlock label="Structure" value={f.structure || "—"} />
                  <MetaBlock label="Layering" value={f.layering || "—"} />
                  {f.fyc_estimate && <MetaBlock label="Projected FYC" value={fmt(f.fyc_estimate)} accent />}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = accent === "gold" ? "gold-text" : accent === "emerald" ? "text-emerald-300" : "";
  return (
    <div>
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className={`font-display text-2xl mt-1 ${cls}`}>{value}</p>
    </div>
  );
}
function MetaBlock({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] eyebrow text-exodus-muted">{label}</p>
      <p className={`text-xs leading-relaxed mt-1 ${accent ? "gold-text font-medium" : ""}`}>{value}</p>
    </div>
  );
}
